import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { lcCoachingApi } from '@/api/lcCoaching'
import { getApiErrorMessage } from '@/utils/apiError'
import { coachingKeys } from '@/hooks/coaching/useCoachingToday'
import type {
  EssentialCheckRatingBatchRequest,
  EssentialCheckRatingRequest,
  LcCoachingGuide,
} from '@/types/coaching'

/**
 * 핵심 체크 5점 채점 mutation (Phase 6a, §5-13=A) — useGradeBuildupStep 미러.
 * - onMutate: 가이드 쿼리(coachingKeys.guide)에서 대상 checkNo 의 score 를 낙관적으로 즉시 패치 + 롤백 스냅샷.
 * - onSuccess: 서버가 돌려준 가이드 페이로드로 통째 교체(권위 재조정).
 * - onError: 롤백 + 에러 토스트.
 */
export function useRateEssentialCheck(learnerId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation('coaching')

  return useMutation({
    mutationFn: (body: EssentialCheckRatingRequest) =>
      lcCoachingApi.rateEssentialCheck(learnerId, body),
    onMutate: async (body) => {
      const key = coachingKeys.guide(learnerId, body.lessonId, body.learningAttemptId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<LcCoachingGuide>(key)
      if (prev) qc.setQueryData<LcCoachingGuide>(key, patchScore(prev, body.checkNo, body.score))
      return { prev, key }
    },
    onError: (err, _body, ctx) => {
      if (ctx?.prev && ctx.key) qc.setQueryData(ctx.key, ctx.prev) // 롤백
      toast.error(getApiErrorMessage(err, t))
    },
    onSuccess: (updated: LcCoachingGuide) => {
      qc.setQueryData(coachingKeys.guide(learnerId, updated.lessonId, updated.learningAttemptId), updated)
      toast.success(t('coachingGuide.essentialCheck.saved'))
    },
  })
}

/**
 * 핵심 체크 일괄 채점 mutation. 화면의 draft 는 호출부가 소유하며, 성공 응답으로만 서버 캐시를 교체한다.
 * 실패 시 draft 를 유지할 수 있도록 낙관적 패치를 하지 않는다.
 */
export function useRateEssentialChecks(learnerId: number) {
  const qc = useQueryClient()
  const { t } = useTranslation('coaching')

  return useMutation({
    mutationFn: (body: EssentialCheckRatingBatchRequest) =>
      lcCoachingApi.rateEssentialChecks(learnerId, body),
    onError: (err) => {
      toast.error(getApiErrorMessage(err, t))
    },
    onSuccess: (updated: LcCoachingGuide) => {
      qc.setQueryData(coachingKeys.guide(learnerId, updated.lessonId, updated.learningAttemptId), updated)
      toast.success(t('coachingGuide.essentialCheck.saved'))
    },
  })
}

/** 낙관적 패치: 대상 checkNo 의 score 만 교체. 매칭 없으면 그대로(onSuccess 가 재조정). */
function patchScore(guide: LcCoachingGuide, checkNo: number, score: number): LcCoachingGuide {
  return {
    ...guide,
    essentialChecks: guide.essentialChecks.map((c) =>
      c.checkNo === checkNo ? { ...c, score } : c,
    ),
  }
}
